import { useEffect, useRef, useState } from "react";
import { FileWarning, LoaderCircle, RefreshCw } from "lucide-react";
import {
  doctrinePdfErrorMessage,
  isDoctrinePdfAuthenticationError,
} from "../lib/doctrine-pdf-auth.js";

const doctrinePdfRangeChunkSize = 1024 * 1024;

function clampPage(value, total) {
  return Math.max(1, Math.min(Number(value) || 1, total || 1));
}

export function DoctrinePdfViewer({ api, pdf: source, title, pageNumber = 1 }) {
  const bodyRef = useRef(null);
  const pageSurfaceRef = useRef(null);
  const canvasRef = useRef(null);
  const textLayerRef = useRef(null);
  const pdfJsRef = useRef(null);
  const resizeFrameRef = useRef(null);
  const authenticationRetryRef = useRef(0);
  const [pdf, setPdf] = useState(null);
  const [renderedPage, setRenderedPage] = useState(null);
  const [loadVersion, setLoadVersion] = useState(0);
  const [renderVersion, setRenderVersion] = useState(0);
  const [state, setState] = useState({
    loading: true,
    error: "",
  });
  const requestedPage = pdf
    ? clampPage(pageNumber, pdf.numPages)
    : Math.max(1, Number(pageNumber) || 1);
  const pageIsLoading = Boolean(pdf && renderedPage !== requestedPage);

  const retryAfterAuthenticationError = (error) => {
    if (
      !isDoctrinePdfAuthenticationError(error)
      || authenticationRetryRef.current >= 1
    ) return false;
    authenticationRetryRef.current += 1;
    setState({
      loading: true,
      error: "",
    });
    setLoadVersion((current) => current + 1);
    return true;
  };

  const retryManually = () => {
    authenticationRetryRef.current = 1;
    setState({
      loading: true,
      error: "",
    });
    setLoadVersion((current) => current + 1);
  };

  useEffect(() => {
    authenticationRetryRef.current = 0;
  }, [source.url]);

  useEffect(() => {
    let active = true;
    let loadedPdf;
    pdfJsRef.current = null;
    setPdf(null);
    setRenderedPage(null);
    const refreshingAuthentication = authenticationRetryRef.current > 0;
    setState({
      loading: true,
      error: "",
    });

    Promise.all([
      import("pdfjs-dist"),
      import("pdfjs-dist/build/pdf.worker.min.mjs?url"),
      api.getDoctrinePdfSource(source.url, {
        refresh: refreshingAuthentication,
      }),
    ])
      .then(async ([pdfJs, workerModule, request]) => {
        const { getDocument, GlobalWorkerOptions } = pdfJs;
        GlobalWorkerOptions.workerSrc = `${workerModule.default}?module=1`;
        loadedPdf = await getDocument({
          ...request,
          disableAutoFetch: true,
          disableStream: true,
          rangeChunkSize: doctrinePdfRangeChunkSize,
          isEvalSupported: false,
        }).promise;
        if (!active) {
          await loadedPdf.destroy();
          return;
        }
        pdfJsRef.current = pdfJs;
        setPdf(loadedPdf);
        setState({
          loading: false,
          error: "",
        });
      })
      .catch((error) => {
        if (!active || retryAfterAuthenticationError(error)) return;
        setState({
          loading: false,
          error: doctrinePdfErrorMessage(
            error,
            "No pudimos abrir el PDF original.",
          ),
        });
      });

    return () => {
      active = false;
      pdfJsRef.current = null;
      if (loadedPdf) void loadedPdf.destroy();
    };
  }, [api, source.url, loadVersion]);

  useEffect(() => {
    const element = bodyRef.current;
    if (!element || !window.ResizeObserver) return undefined;
    const observer = new ResizeObserver(() => {
      if (resizeFrameRef.current) window.cancelAnimationFrame(resizeFrameRef.current);
      resizeFrameRef.current = window.requestAnimationFrame(() => {
        resizeFrameRef.current = null;
        setRenderVersion((current) => current + 1);
      });
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      if (resizeFrameRef.current) window.cancelAnimationFrame(resizeFrameRef.current);
    };
  }, [pdf]);

  useEffect(() => {
    if (
      !pdf
      || !pdfJsRef.current?.TextLayer
      || !canvasRef.current
      || !textLayerRef.current
      || !pageSurfaceRef.current
      || !bodyRef.current
    ) return undefined;
    let active = true;
    let renderTask;
    let textLayer;
    setState((current) => ({ ...current, error: "" }));

    pdf.getPage(requestedPage)
      .then((pdfPage) => {
        if (!active) return null;
        const baseViewport = pdfPage.getViewport({ scale: 1 });
        const availableWidth = Math.max(
          Math.min(bodyRef.current.clientWidth - 40, 1120),
          260,
        );
        const viewport = pdfPage.getViewport({ scale: availableWidth / baseViewport.width });
        const pageSurface = pageSurfaceRef.current;
        const canvas = canvasRef.current;
        const textLayerElement = textLayerRef.current;
        const context = canvas.getContext("2d");
        const pixelRatio = window.devicePixelRatio || 1;
        pageSurface.style.width = `${viewport.width}px`;
        pageSurface.style.height = `${viewport.height}px`;
        pageSurface.style.setProperty("--scale-factor", String(viewport.scale));
        canvas.width = Math.floor(viewport.width * pixelRatio);
        canvas.height = Math.floor(viewport.height * pixelRatio);
        canvas.style.width = `${viewport.width}px`;
        canvas.style.height = `${viewport.height}px`;
        textLayerElement.replaceChildren();
        renderTask = pdfPage.render({
          canvasContext: context,
          viewport,
          transform: pixelRatio === 1
            ? null
            : [pixelRatio, 0, 0, pixelRatio, 0, 0],
        });
        textLayer = new pdfJsRef.current.TextLayer({
          textContentSource: pdfPage.streamTextContent({
            includeMarkedContent: true,
            disableNormalization: true,
          }),
          container: textLayerElement,
          viewport,
        });
        return Promise.all([renderTask.promise, textLayer.render()]);
      })
      .then(() => {
        if (active) {
          authenticationRetryRef.current = 0;
          setRenderedPage(requestedPage);
        }
      })
      .catch((error) => {
        if (
          !active
          || error?.name === "RenderingCancelledException"
          || error?.name === "AbortException"
        ) return;
        if (retryAfterAuthenticationError(error)) return;
        setState((current) => ({
          ...current,
          error: doctrinePdfErrorMessage(
            error,
            "No pudimos mostrar esta página.",
          ),
        }));
      });

    return () => {
      active = false;
      renderTask?.cancel();
      textLayer?.cancel();
    };
  }, [pdf, renderVersion, requestedPage]);

  if (state.loading) {
    return (
      <div className="doctrine-pdf-state" role="status">
        <LoaderCircle className="spin" size={22} />
        Cargando la página {requestedPage}…
      </div>
    );
  }

  if (state.error || !pdf) {
    return (
      <div className="doctrine-pdf-state is-error" role="alert">
        <FileWarning size={24} />
        <strong>No pudimos abrir esta página del PDF</strong>
        <span>{state.error || "El archivo no está disponible."} Podés consultar el texto extraído.</span>
        <button type="button" onClick={retryManually}>
          <RefreshCw size={15} /> Reintentar
        </button>
      </div>
    );
  }

  return (
    <div
      className="doctrine-pdf-page"
      ref={bodyRef}
      aria-label={`Página ${requestedPage} del PDF original de ${title}`}
    >
      {pageIsLoading && (
        <span className="doctrine-pdf-rendering" role="status" aria-live="polite">
          <LoaderCircle className="spin" size={16} /> Cargando la página {requestedPage}…
        </span>
      )}
      <div className="doctrine-pdf-surface" ref={pageSurfaceRef}>
        <canvas ref={canvasRef} aria-hidden="true" />
        <div
          className="doctrine-pdf-text-layer"
          ref={textLayerRef}
          aria-label={`Texto seleccionable de la página ${renderedPage || requestedPage}`}
        />
      </div>
    </div>
  );
}
